/**
 * ConditionalVisibilityControls Component
 *
 * Provides UI controls for Custom Survey conditional question visibility.
 * Allows users to configure a question to only show when a specific answer
 * (or one of several answers) is selected on a previous Dropdown or Checkbox question.
 *
 * Only renders when there is at least one preceding Dropdown/Checkbox question.
 */

import { FormFieldType, IFormField } from '@ViewModels';
import * as React from 'react';
import { XIcon } from '../../../../../../components/svgs/icons/XIcon';
import { brandPrimary, charmGray } from '../../../../../../styles/colors';
import { Checkbox } from '../../../../../../components-v2/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../components-v2/Select';
import { usePreviewMode } from '../../../contexts/PreviewModeContext';
import { styleSheet } from '../styles';
import { ConditionalVisibilityProps } from '../types';

/**
 * Derives the list of preceding fields that are Dropdown or Checkbox type
 */
const getPrecedingOptionFields = (allFields: IFormField<string>[], fieldIndex: number) => {
	return allFields
		.slice(0, fieldIndex)
		.reduce<{ field: IFormField<string>; originalIndex: number }[]>((acc, field, idx) => {
			if (field.fieldType === FormFieldType.Option || field.fieldType === FormFieldType.MultipleOptions) {
				acc.push({ field, originalIndex: idx });
			}
			return acc;
		}, []);
};

interface QuestionOption {
	title: string;
	value: string;
}

interface AnswerOption {
	title: string;
	value: string;
}

/**
 * Builds question options from preceding option-type fields
 */
const buildQuestionOptions = (
	precedingFields: { field: IFormField<string>; originalIndex: number }[]
): QuestionOption[] => {
	return precedingFields.map(({ field, originalIndex }) => ({
		title: `Question ${originalIndex + 1}`,
		value: field.id,
	}));
};

/**
 * Builds answer options from a field's options
 */
const buildAnswerOptions = (allFields: IFormField<string>[], selectedQuestionId?: string): AnswerOption[] => {
	if (!selectedQuestionId) return [];
	const selectedField = allFields.find(f => f.id === selectedQuestionId);
	if (!selectedField?.options) return [];
	return selectedField.options
		.filter(opt => opt.label?.trim())
		.map(opt => ({
			title: opt.label!,
			value: opt.id!,
		}));
};

/**
 * Controls for managing conditional question visibility based on a previous question's answer
 */
export const ConditionalVisibilityControls: React.FC<ConditionalVisibilityProps> = ({
	field,
	fieldIndex,
	allFields,
	state,
	onToggle,
	onQuestionSelect,
	onAnswerSelect,
	onAnswerAdd,
	onAnswerRemove,
}) => {
	const { isPreviewOnly } = usePreviewMode();

	const isEnabled = state?.enabled ?? false;
	const selectedQuestionId = state?.dependsOnFieldId;
	const selectedOptionIds = state?.dependsOnOptionIds;

	const precedingOptionFields = React.useMemo(
		() => getPrecedingOptionFields(allFields, fieldIndex),
		[allFields, fieldIndex]
	);

	const questionOptions = React.useMemo(() => buildQuestionOptions(precedingOptionFields), [precedingOptionFields]);

	const selectedQuestionLabel = React.useMemo(
		() => questionOptions.find(opt => opt.value === selectedQuestionId)?.title,
		[questionOptions, selectedQuestionId]
	);

	const answerOptions = React.useMemo(
		() => buildAnswerOptions(allFields, selectedQuestionId),
		[allFields, selectedQuestionId]
	);

	const handleCheckboxChange = React.useCallback(
		(checked: boolean | 'indeterminate') => {
			onToggle(field.id, Boolean(checked));
		},
		[field.id, onToggle]
	);

	const handleQuestionChange = React.useCallback(
		(value: string) => {
			onQuestionSelect(field.id, value);
		},
		[field.id, onQuestionSelect]
	);

	const handleAddAnswer = React.useCallback(() => {
		onAnswerAdd(field.id);
	}, [field.id, onAnswerAdd]);

	const isQuestionSelectDisabled = !isEnabled || isPreviewOnly;
	const isAnswerSelectDisabled = !isEnabled || !selectedQuestionId || isPreviewOnly;

	// Don't render if no preceding dropdown/checkbox questions exist
	if (precedingOptionFields.length === 0) {
		return null;
	}

	// Determine answer rows to display
	const answerCount = selectedOptionIds?.length ?? 0;
	const hasFirstAnswer = answerCount > 0;
	const additionalAnswerIndices = hasFirstAnswer ? selectedOptionIds!.slice(1).map((_, i) => i + 1) : [];

	return (
		<div style={{ padding: '10px 0', fontSize: 14 }}>
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
					<Checkbox
						checked={isEnabled}
						id={`conditional-visibility-${fieldIndex}-checkbox`}
						onCheckedChange={handleCheckboxChange}
						disabled={isPreviewOnly}
					/>
					<span>Only show when</span>
					<Select
						value={selectedQuestionId ?? ''}
						onValueChange={handleQuestionChange}
						disabled={isQuestionSelectDisabled}
					>
						<SelectTrigger styles={styleSheet.conditionalVisibilityQuestionDropdown}>
							<SelectValue placeholder='Select question'>{selectedQuestionLabel}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{questionOptions.map(opt => (
								<SelectItem key={opt.value} value={opt.value}>
									{opt.title}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<span>is</span>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<AnswerRow
						answerOptions={answerOptions}
						selectedOptionId={hasFirstAnswer ? selectedOptionIds![0] : undefined}
						answerIndex={0}
						fieldId={field.id}
						disabled={isAnswerSelectDisabled}
						isPreviewOnly={isPreviewOnly}
						onAnswerSelect={onAnswerSelect}
					/>
					{additionalAnswerIndices.map(answerIdx => (
						<div key={`or-row-${answerIdx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span
								style={{
									color: charmGray,
									fontStyle: 'italic',
									position: 'absolute',
									transform: 'translateX(calc(-100% - 8px))',
								}}
							>
								-or-
							</span>
							<AnswerRow
								answerOptions={answerOptions}
								selectedOptionId={selectedOptionIds![answerIdx]}
								answerIndex={answerIdx}
								fieldId={field.id}
								disabled={isAnswerSelectDisabled}
								isPreviewOnly={isPreviewOnly}
								onAnswerSelect={onAnswerSelect}
								showRemove
								onRemove={() => onAnswerRemove(field.id, answerIdx)}
							/>
						</div>
					))}
					{isEnabled && !!selectedQuestionId && !isPreviewOnly && (
						<button
							type='button'
							onClick={handleAddAnswer}
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								color: brandPrimary,
								padding: 0,
								fontSize: 14,
								textAlign: 'left',
							}}
						>
							+ Add Answer
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

/**
 * Single answer row with select and optional X button
 */
interface AnswerRowProps {
	answerOptions: AnswerOption[];
	selectedOptionId?: string;
	answerIndex: number;
	fieldId: string;
	disabled: boolean;
	isPreviewOnly: boolean;
	onAnswerSelect: (fieldId: string, answerIndex: number, optionId: string) => void;
	showRemove?: boolean;
	onRemove?: () => void;
}

const AnswerRow: React.FC<AnswerRowProps> = React.memo(
	({
		answerOptions,
		selectedOptionId,
		answerIndex,
		fieldId,
		disabled,
		isPreviewOnly,
		onAnswerSelect,
		showRemove,
		onRemove,
	}) => {
		const handleAnswerChange = React.useCallback(
			(value: string) => {
				onAnswerSelect(fieldId, answerIndex, value);
			},
			[fieldId, answerIndex, onAnswerSelect]
		);

		const selectedAnswerLabel = React.useMemo(
			() => answerOptions.find(opt => opt.value === selectedOptionId)?.title,
			[answerOptions, selectedOptionId]
		);

		return (
			<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<Select value={selectedOptionId ?? ''} onValueChange={handleAnswerChange} disabled={disabled}>
					<SelectTrigger styles={styleSheet.conditionalVisibilityAnswerDropdown}>
						<SelectValue placeholder='Select answer'>{selectedAnswerLabel}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{answerOptions.map(opt => (
							<SelectItem key={opt.value} value={opt.value}>
								{opt.title}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{showRemove && !isPreviewOnly && (
					<button
						type='button'
						onClick={onRemove}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							padding: 0,
							lineHeight: 1,
							display: 'inline-flex',
							alignItems: 'center',
							color: charmGray,
						}}
						title='Remove this answer'
					>
						<XIcon width={16} height={16} />
					</button>
				)}
			</div>
		);
	}
);

AnswerRow.displayName = 'AnswerRow';
